import * as React from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem } from 'polished';
import { FlexCol } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { TextLink, TitleLinkMedium } from '../General/Text';
import { getImagePath } from '../../../utilities';

export interface Props {
  url: string;
  title: string;
  imageSrc: string;
  alt?: string;
  buttonColor?: string;
  bgColor?: string;
  first?: boolean;
  description: string;
}

function Card({
  imageSrc,
  url,
  title,
  alt,
  buttonColor,
  bgColor,
  first,
  description,
}: Props) {
  const maxLengthOfDescription = 150;
  const placeholderImgSrc = '/images/zlatapriadka/placeholder.png';
  const apiUrl = process.env.REACT_APP_API_BASE_URL!;

  const getDescription = (): string => {
    if (description.length > maxLengthOfDescription) {
      return `${description.substring(0, maxLengthOfDescription)}...`;
    }
    return description;
  };

  return (
    <Wrapper bg={bgColor} first={first}>
      <ImageWrapper to={url}>
        <Image
          src={
            imageSrc
              ? getImagePath(
                  apiUrl,
                  imageSrc,
                  {
                    width: 448,
                    height: 336,
                  },
                  false,
                  false,
                  true,
                )
              : placeholderImgSrc
          }
          alt={alt}
        />
      </ImageWrapper>
      <TextButtonWrapper>
        <TextLinkTitle to={url}>{title}</TextLinkTitle>
        <TextLinkDescription
          to={url}
          dangerouslySetInnerHTML={{
            __html: typeof description === 'object' ? '' : getDescription(),
          }}
        />
      </TextButtonWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)<{ first: boolean; bg: string }>`
  width: 100%;
  border-radius: ${rem(4)};
  background: ${({ theme }) => theme.colors.white};
  grid-column-start: ${props => (props.first ? 2 : '')};
  flex: 1 1 50%;
  margin: 0 ${rem(16)};
  max-width: ${rem(448)};
  transition: all 0.2s ease-out;
  margin-bottom: ${rem(32)};

  &:hover,
  &:focus {
    transition: all 0.2s ease-out;
    box-shadow: 0 ${rem(2)} ${rem(32)} #0000000a;
  }

  ${({ theme }) => theme.media(0, 'sm')} {
    &:hover,
    &:focus {
      box-shadow: none;
    }
  }

  ${({ theme }) => theme.mediab.m580`
    flex-basis: 100%;
    max-width: intial;
    margin: 0;
    margin-bottom: ${rem(32)};
  `}
`;

const ImageWrapper = styled(Link)`
  width: 100%;
`;

const Image = styled.img`
  width: 100%;
  max-height: ${rem(336)};
  border-radius: ${rem(8)} ${rem(8)} 0 0;
  object-fit: cover;

  ${({ theme }) => theme.mediab.l1150`
    height: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.m810`
    height: ${rem(216)};
  `}
`;

const TextButtonWrapper = styled(FlexCol)`
  justify-content: flex-start;
  height: 100%;
  max-height: 100%;
  padding: ${rem(32)};

  ${({ theme }) => theme.mediab.m580`
    padding: ${rem(26)} ${rem(16)} 0;
  `}
`;

const TextLinkTitle = styled(TitleLinkMedium)`
  margin-bottom: ${rem(24)};
  text-transform: uppercase;

  ${({ theme }) => theme.mediab.l925`
     margin-bottom: ${rem(16)};
  `}
`;

const TextLinkDescription = styled(TextLink)`
  ${({ theme }) => theme.mediab.l925`
     margin: 0;
  `}

  * {
    margin: 0;
  }
`;

export { Card as CardBlog };
